import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, XCircle, ChevronRight, RefreshCw } from "lucide-react";
import Pager, { usePager } from "@/components/Pager";
import TaxonomyPicker from "@/components/TaxonomyPicker";

const STAGE_BADGE = {
  "Pending Admin Review": "border-amber-200 bg-amber-50 text-amber-700",
  "Awaiting Mentor Assignment": "border-indigo-200 bg-indigo-50 text-indigo-700",
};

const REQ_STATUS = {
  pending_mentor: { label: "awaiting mentor", cls: "border-amber-200 bg-amber-50 text-amber-700" },
  accepted: { label: "mentor accepted", cls: "border-emerald-200 bg-emerald-50 text-emerald-700" },
  rejected: { label: "mentor declined", cls: "border-rose-200 bg-rose-50 text-rose-700" },
};

export default function AdminPendingIdeasPage() {
  const [ideas, setIdeas] = useState([]);
  const pager = usePager(ideas, 15);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [reviewForm, setReviewForm] = useState({
    feasibility_idea: 75,
    feasibility_technical: 75,
    feasibility_methodological: 70,
    feasibility_data: 70,
    feasibility_timeline: 70,
    category: "",
    taxonomy_node_id: "",
    priority: "medium",
    publication_time_weeks: "",
    admin_notes: "",
  });
  const [taxonomy, setTaxonomy] = useState([]);
  const [suggestions, setSuggestions] = useState({});
  const [busy, setBusy] = useState({});

  useEffect(() => {
    api.get("/taxonomy", { params: { domain: "research_ideas" } })
      .then((r) => setTaxonomy(r.data || []))
      .catch(() => setTaxonomy([]));
  }, []);

  const overallFeasibility = useMemo(() => {
    const subs = [reviewForm.feasibility_idea, reviewForm.feasibility_technical, reviewForm.feasibility_methodological, reviewForm.feasibility_data, reviewForm.feasibility_timeline];
    const valid = subs.filter((s) => typeof s === "number" && !Number.isNaN(s));
    if (!valid.length) return 70;
    return Math.round(valid.reduce((a, b) => a + b, 0) / valid.length);
  }, [reviewForm]);

  const estimatedWeeks = useMemo(() => {
    if (reviewForm.publication_time_weeks) return reviewForm.publication_time_weeks;
    return Math.max(8, Math.min(28, 30 - Math.round(overallFeasibility * 0.20)));
  }, [reviewForm.publication_time_weeks, overallFeasibility]);

  const load = async () => {
    setLoading(true);
    const r = await api.get("/admin/ideas/pending");
    setIdeas(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const fetchSuggestions = async (id) => {
    try {
      const r = await api.get(`/admin/ideas/${id}/suggest-mentors`);
      setSuggestions((s) => ({ ...s, [id]: r.data }));
    } catch (e) { toast.error("Failed to load mentor suggestions"); }
  };

  const approve = async (id) => {
    setBusy((b) => ({ ...b, [id]: "approving" }));
    try {
      const payload = { ...reviewForm, feasibility_score: overallFeasibility };
      await api.post(`/admin/ideas/${id}/approve`, payload);
      toast.success("Approved — pick a mentor to send the project to.");
      await load();
      await fetchSuggestions(id);
      setReviewing(null);
    } catch (e) { toast.error("Approval failed"); }
    finally { setBusy((b) => ({ ...b, [id]: null })); }
  };

  const reject = async (id) => {
    if (!window.confirm("Reject this idea?")) return;
    try {
      await api.post(`/admin/ideas/${id}/reject`, { body: reviewForm.admin_notes || "Rejected by admin" });
      toast.success("Idea rejected");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const sendToMentor = async (id, mentor_id) => {
    setBusy((b) => ({ ...b, [`${id}-${mentor_id}`]: true }));
    try {
      await api.post(`/admin/ideas/${id}/send-to-mentor`, { mentor_id, note: "Please review" });
      toast.success("Request sent — waiting on mentor's decision");
      // refresh both list and suggestions to reflect change
      await Promise.all([load(), fetchSuggestions(id)]);
    } catch (e) { toast.error("Failed"); }
    finally { setBusy((b) => ({ ...b, [`${id}-${mentor_id}`]: false })); }
  };

  return (
    <div data-testid="admin-pending-ideas-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">PENDING IDEA REVIEW</div>
      <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Review submitted <em className="italic text-kb-primary">ideas</em></h1>
      <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">
        Approve to set feasibility / category / priority — then assign a mentor inline. If the mentor declines, the idea stays here until you route it to another mentor who accepts.
      </p>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> :
        ideas.length === 0 ? <div className="mt-10 text-center py-16 border border-dashed border-slate-300 rounded-sm bg-white text-sm text-kb-ink-muted">No ideas awaiting action.</div> :
        (() => {
          return (
        <div className="mt-8 space-y-4">
          {pager.pageItems.map((i) => {
            const latest = i.latest_mentor_request;
            const awaitingMentor = i.status === "Awaiting Mentor Assignment";
            const hasOpenRequest = latest && latest.status === "pending_mentor";
            const showSuggestions = suggestions[i.id] || (awaitingMentor && !hasOpenRequest);
            return (
              <div key={i.id} className="kb-card p-5 lg:p-6" data-testid={`pending-idea-${i.id}`}>
                <div className="md:flex md:flex-wrap md:items-start md:justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted break-words">{i.id} · {i.domain} · by {i.user_name}</div>
                    <h3 className="font-serif text-lg lg:text-xl text-kb-ink mt-1 break-words">{i.title}</h3>
                    {/* Contact + stage chips (B.1-3) so admins can tell at a glance
                        which review flow this needs and how to reach the author. */}
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {i.stage && (
                        <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-kb-primary/10 text-kb-primary border border-kb-primary/20" data-testid={`pending-${i.id}-stage`}>
                          Stage · {String(i.stage).replace(/_/g, " ")}
                        </span>
                      )}
                      {i.whatsapp && (
                        <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-emerald-50 text-emerald-700 border border-emerald-200">
                          WhatsApp · {i.whatsapp}
                        </span>
                      )}
                      {i.preferred_contact && (
                        <span className="kb-mono text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-slate-100 text-kb-ink-muted border border-slate-200">
                          Prefers · {String(i.preferred_contact).replace(/_/g, " ")}
                        </span>
                      )}
                    </div>
                    {/* Full submission text — collapsed by default so the page
                        stays scannable with many pending items. */}
                    <details className="mt-3 group" data-testid={`pending-${i.id}-full`}>
                      <summary className="cursor-pointer kb-mono text-[10px] uppercase tracking-wider text-kb-primary hover:underline list-none inline-flex items-center gap-1">
                        <span className="group-open:hidden">Show full submission ▸</span>
                        <span className="hidden group-open:inline">Hide ▾</span>
                      </summary>
                      <div className="mt-2 text-sm text-kb-ink whitespace-pre-wrap break-words bg-slate-50 border border-slate-200 rounded-sm p-3">
                        {i.description || i.abstract}
                      </div>
                      {(i.methodology || i.reference_papers || i.notes || i.dataset_link || i.target_venue || i.target_quartile) && (
                        <dl className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                          {i.methodology && (<div><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Methodology</dt><dd className="whitespace-pre-wrap break-words">{i.methodology}</dd></div>)}
                          {i.reference_papers && (<div><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Reference papers</dt><dd className="whitespace-pre-wrap break-words">{i.reference_papers}</dd></div>)}
                          {i.notes && (<div><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Notes</dt><dd className="whitespace-pre-wrap break-words">{i.notes}</dd></div>)}
                          {i.dataset_link && (<div><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Dataset link</dt><dd className="break-words"><a className="text-kb-primary underline" href={i.dataset_link} target="_blank" rel="noreferrer">{i.dataset_link}</a></dd></div>)}
                          {(i.target_venue || i.target_quartile) && (<div><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Target</dt><dd>{[i.target_quartile, i.target_venue].filter(Boolean).join(" · ")}</dd></div>)}
                          {(i.keywords || []).length > 0 && (<div className="sm:col-span-2"><dt className="kb-mono text-[9px] uppercase text-kb-ink-muted">Keywords</dt><dd>{(i.keywords || []).join(", ")}</dd></div>)}
                        </dl>
                      )}
                    </details>
                    {awaitingMentor && (
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mt-2">
                        feasibility {i.feasibility_score} · {i.category} · priority {i.priority}
                      </div>
                    )}
                  </div>
                  <span className={`mt-3 md:mt-0 inline-block self-start kb-mono text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm border whitespace-nowrap ${STAGE_BADGE[i.status] || "border-slate-200"}`}>{i.status}</span>
                </div>

                {/* Latest mentor request status */}
                {latest && (
                  <div className="mt-4 flex items-center gap-3 flex-wrap text-xs" data-testid={`pending-${i.id}-latest-req`}>
                    <span className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">LAST SENT TO</span>
                    <span className="text-kb-ink font-medium">{latest.mentor_name}</span>
                    <span className={`kb-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${REQ_STATUS[latest.status]?.cls || "border-slate-200"}`}>
                      {REQ_STATUS[latest.status]?.label || latest.status}
                    </span>
                    {latest.mentor_note && <span className="text-kb-ink-muted italic">&ldquo;{latest.mentor_note}&rdquo;</span>}
                  </div>
                )}

                {/* Action area: review (pending) OR mentor selector (approved & no open request) */}
                {i.status === "Pending Admin Review" && reviewing !== i.id && (
                  <div className="mt-4 flex gap-2 flex-wrap">
                    <button onClick={() => setReviewing(i.id)} className="kb-btn-primary text-sm" data-testid={`pending-${i.id}-review`}>
                      Review & approve <ChevronRight className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {i.status === "Pending Admin Review" && reviewing === i.id && (
                  <div className="mt-5 pt-5 border-t border-slate-200 space-y-3">
                    {/* Multi-axis feasibility */}
                    <div>
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted mb-2">Feasibility breakdown (0–100 each)</div>
                      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                        {[
                          ["feasibility_idea", "Idea"],
                          ["feasibility_technical", "Technical"],
                          ["feasibility_methodological", "Method"],
                          ["feasibility_data", "Data"],
                          ["feasibility_timeline", "Timeline"],
                        ].map(([k, lbl]) => (
                          <div key={k}>
                            <label className="kb-mono text-[9px] uppercase tracking-wider text-kb-ink-light">{lbl}</label>
                            <input
                              type="number" min="0" max="100"
                              className="kb-input text-sm mt-1"
                              value={reviewForm[k]}
                              onChange={(e) => setReviewForm({ ...reviewForm, [k]: (e.target.value === "" ? "" : Math.max(0, Math.min(100, parseInt(e.target.value, 10) || 0))) })}
                              data-testid={`pending-${i.id}-${k}`}
                            />
                          </div>
                        ))}
                      </div>
                      <div className="mt-2 kb-mono text-[10px] text-kb-ink-muted">
                        Overall feasibility: <span className="text-kb-purple font-bold">{overallFeasibility}</span> / 100
                        &nbsp;·&nbsp;
                        Estimated publication time: <span className="text-kb-purple font-bold">{estimatedWeeks} weeks</span>
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-3">
                        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Final Category (taxonomy)</label>
                        <div className="mt-1.5">
                          <TaxonomyPicker
                            domain="research_ideas"
                            value={reviewForm.taxonomy_node_id || null}
                            onChange={(id) => {
                              const node = taxonomy.find((t) => t.id === id);
                              setReviewForm({ ...reviewForm, taxonomy_node_id: id || "", category: node?.name || reviewForm.category });
                            }}
                          />
                        </div>
                        {i.taxonomy_node_id && (
                          <div className="kb-mono text-[9px] text-kb-ink-light mt-1">
                            Client suggested: {taxonomy.find((t) => t.id === i.taxonomy_node_id)?.name || i.taxonomy_node_id}
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Publication time (weeks)</label>
                        <input type="number" min="1" max="104" className="kb-input mt-1.5"
                          placeholder={`auto: ${estimatedWeeks}`}
                          value={reviewForm.publication_time_weeks}
                          onChange={(e) => setReviewForm({ ...reviewForm, publication_time_weeks: (e.target.value === "" ? "" : (parseInt(e.target.value, 10) || "")) })} />
                      </div>
                      <div>
                        <label className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">Priority</label>
                        <select className="kb-input mt-1.5" value={reviewForm.priority}
                          onChange={(e) => setReviewForm({ ...reviewForm, priority: e.target.value })}
                          data-testid={`pending-${i.id}-priority`}>
                          <option>high</option><option>medium</option><option>low</option>
                        </select>
                      </div>
                    </div>
                    <textarea rows={2} className="kb-input resize-none" placeholder="Admin notes (optional)"
                      value={reviewForm.admin_notes} onChange={(e) => setReviewForm({ ...reviewForm, admin_notes: e.target.value })} />
                    <div className="flex gap-2">
                      <button onClick={() => approve(i.id)} disabled={busy[i.id] === "approving"} className="kb-btn-primary text-sm" data-testid={`pending-${i.id}-approve`}>
                        <CheckCircle2 className="w-3.5 h-3.5" /> {busy[i.id] === "approving" ? "Approving…" : "Approve & pick mentor"}
                      </button>
                      <button onClick={() => reject(i.id)} className="kb-btn-outline text-sm" data-testid={`pending-${i.id}-reject`}>
                        <XCircle className="w-3.5 h-3.5" /> Reject
                      </button>
                      <button onClick={() => setReviewing(null)} className="text-sm text-kb-ink-muted">Cancel</button>
                    </div>
                  </div>
                )}

                {/* Mentor picker */}
                {awaitingMentor && !hasOpenRequest && (
                  <div className="mt-5 pt-5 border-t border-slate-200">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div className="kb-mono text-[10px] uppercase tracking-wider text-kb-ink-muted">
                        {suggestions[i.id] ? "SUGGESTED MENTORS" : "PICK A MENTOR TO SEND THE PROJECT"}
                      </div>
                      <button onClick={() => fetchSuggestions(i.id)} className="kb-mono text-[10px] uppercase tracking-wider text-kb-primary inline-flex items-center gap-1.5" data-testid={`pending-${i.id}-refresh-suggestions`}>
                        <RefreshCw className="w-3 h-3" /> {suggestions[i.id] ? "Refresh" : "Load suggestions"}
                      </button>
                    </div>
                    {suggestions[i.id] && (
                      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {suggestions[i.id].length === 0 && (
                          <div className="col-span-full text-sm text-kb-ink-muted">All matched mentors have declined this idea. Please re-route manually or contact mentors directly.</div>
                        )}
                        {suggestions[i.id].map((m, mi) => (
                          <div key={m.id} className="border border-slate-200 rounded-sm p-3" data-testid={`suggest-${i.id}-${m.id}`}>
                            <div className="flex items-center gap-2">
                              <img src={m.photo_url} alt="" className="w-8 h-8 rounded-full" />
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-kb-ink truncate">{m.name}</div>
                                <div className="kb-mono text-[10px] text-kb-ink-light truncate">{m.affiliation}</div>
                              </div>
                              {mi === 0 && <span className="kb-mono text-[9px] text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded-sm">TOP</span>}
                            </div>
                            <div className="mt-2 kb-mono text-[10px] text-kb-ink-muted">match {m.match_score} · {m.active_projects} active · h-index {m.h_index}</div>
                            <button onClick={() => sendToMentor(i.id, m.id)} disabled={!!busy[`${i.id}-${m.id}`]} className="kb-btn-primary text-xs w-full mt-3 justify-center" data-testid={`send-${i.id}-${m.id}`}>
                              {busy[`${i.id}-${m.id}`] ? "Sending…" : "Send request"}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
          <Pager {...pager} label="ideas" testId="admin-ideas-pager" />
        </div>
        );
        })()
      }
    </div>
  );
}
